import React, { memo, useCallback, useMemo, useState } from 'react';

import {
	Chat,
	GetVideoQuery,
	Maybe,
	Message,
	MessageResults,
	MessageType,
	Reaction,
	User,
	UserChatStat,
	VideoChat,
} from '@/graphql';
import { classNames } from '@/helpers/classNames';

import styles from './ChatContainer.module.scss';
import {
	Chatbar,
	ChatbarCommonMessageTypes,
	ChatbarProps,
} from './components/ChatBar';
import { ChatList } from './components/ChatList';
import messageContentStyles from './components/ChatList/components/RenderMessage/components/MessageContent/MessageContent.module.scss';
import { FocusedOptionsBar } from './components/FocusedOptionsBar/FocusedOptionsBar';
import { ReactionsSheet } from './components/ReactionsSheet/ReactionsSheet';

// items: Array<{
//   __typename?: 'Message';
//   content: string;
//   createdAt: string;
//   id: string;
//   user?: {
//     __typename?: 'User';
//     displayName?: string | null;
//     id: string;
//   } | null;
// }>;

interface ChatContainerContext {
	openReactionsSheet: (reactions: Reaction[]) => void;
	closeReactionsSheet: () => void;
	typingUserIds?: string[];
	members?: Array<{ id: string; displayName?: string | null }>;
}

const defaultValue: ChatContainerContext = {
	openReactionsSheet: () => {},
	closeReactionsSheet: () => {},
};

export const ChatContainerContext =
	React.createContext<ChatContainerContext>(defaultValue);

const FocusedOptionsBarMemoized = memo(FocusedOptionsBar);
const ChatbarMemoized = memo(Chatbar);

export type ChatContainerSendMessageFn = (
	message: string,
	type?: MessageType,
	metadata?: string,
) => Promise<any>;

export type ChatContainerMessage = Pick<
	Message,
	'type' | 'metadata' | 'content' | 'createdAt' | 'id' | 'userId'
> & {
	user?: Maybe<Pick<User, 'displayName' | 'id'>>;
	reactions?: Reaction[];
};

export interface IChatContainerProps {
	className?: string;
	chat?: Maybe<{
		id: string;
		type: string;
		messages?: Maybe<{
			items: Array<ChatContainerMessage>;
			totalCount: number;
		}>;
		userIds?: string[];
	}>;
	userStats?: UserChatStat[] | null;
	sendMessage: ChatContainerSendMessageFn;
	fetchPreviousMessages: () => Promise<any>;
	onMessageInputThrottled?: ChatbarProps['onMessageInputThrottled'];
	previousMessagesLoading: boolean;
	commonMessageTypes?: ChatbarCommonMessageTypes;
	hasMoreMessages: boolean;
	tralingChatBarComponent?: React.ReactElement;
	typingUserIds?: string[];
	members?: Array<{ id: string; displayName?: string | null }>;
	isGated?: boolean;
	isAdmin?: boolean;
}

export const ChatContainer: React.FC<IChatContainerProps> = ({
	className,
	chat,
	userStats,
	commonMessageTypes,
	sendMessage,
	fetchPreviousMessages,
	onMessageInputThrottled,
	previousMessagesLoading,
	hasMoreMessages,
	tralingChatBarComponent,
	typingUserIds,
	members,
	isGated,
	isAdmin,
}) => {
	const [focusedMessageId, setFocusedMessageId] = useState<
		string | undefined
	>();
	const [
		isReactionsSheetOpenWithReactions,
		setIsReactionsSheetOpenWithReactions,
	] = useState<Reaction[] | null>(null);

	const focusedMessage = useMemo(() => {
		if (chat?.messages?.items) {
			return chat.messages.items.find((m) => m.id === focusedMessageId);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [focusedMessageId, chat?.messages?.items.length]);

	const openReactionsSheet = (reactions: Reaction[]) => {
		setIsReactionsSheetOpenWithReactions(reactions);
	};

	const closeReactionsSheet = () => {
		setIsReactionsSheetOpenWithReactions(null);
	};

	return (
		<ChatContainerContext.Provider
			value={{
				openReactionsSheet,
				closeReactionsSheet,
				typingUserIds,
				members,
			}}
		>
			<div className={`${styles['chat-container']} ${className || ''}`}>
				<div className={styles['main']}>
					<ChatList
						chat={chat}
						userStats={userStats}
						fetchPreviousMessages={fetchPreviousMessages}
						previousMessagesLoading={previousMessagesLoading}
						hasMoreMessages={hasMoreMessages}
						focusedMessageId={focusedMessageId}
						setFocusedMessageId={setFocusedMessageId}
						isGated={isGated}
						isAdmin={isAdmin}
					/>
				</div>
				<footer className={styles['footer']}>
					<div
						className={classNames([
							styles['typing-status'],
							!!typingUserIds?.length && styles['active'],
						])}
					>
						<div
							className={classNames([
								styles['content'],
								messageContentStyles['content'],
								messageContentStyles['content--received'],
							])}
						>
							<div className={styles['indicator']}>
								<div className={styles['tidot']} />
								<div className={styles['tidot']} />
								<div className={styles['tidot']} />
							</div>
						</div>
					</div>

					<ChatbarMemoized
						tralingComponent={tralingChatBarComponent}
						onMessageSend={sendMessage}
						commonMessageTypes={commonMessageTypes}
						hidden={!!focusedMessageId}
						onMessageInputThrottled={onMessageInputThrottled}
						members={members}
						chatType={chat?.type}
					/>

					<FocusedOptionsBarMemoized
						active={!!focusedMessageId}
						focusedMessage={focusedMessage}
					/>
				</footer>

				{isReactionsSheetOpenWithReactions && (
					<ReactionsSheet
						reactions={isReactionsSheetOpenWithReactions}
					/>
				)}
			</div>
		</ChatContainerContext.Provider>
	);
};
